import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { camerasAPI } from '../../utils/api';
import {
  DEFAULT_PRESENCE_SETTINGS,
  normalizePresenceSettings,
  PresenceScheduleInterval,
  PresenceSettings,
  PresenceZone,
} from '../../utils/presence';
import '../RoiSelector/RoiSelector.css';
import './PresenceSettingsModal.css';

const presenceIconUrl = `${process.env.PUBLIC_URL}/icons/modals/people.svg`;
const roiBtnIcon = {
  save: `${process.env.PUBLIC_URL}/icons/svg_icon/save.svg`,
  clear: `${process.env.PUBLIC_URL}/icons/svg_icon/clear.svg`,
  cancel: `${process.env.PUBLIC_URL}/icons/svg_icon/cancel.svg`,
} as const;

function resolveStreamUrl(webrtcOut?: string | null, hlsOut?: string | null): string | null {
  let url = webrtcOut || hlsOut || null;
  if (!url) return null;
  if (url.startsWith('https://') && url.includes('192.168.')) {
    url = url.replace('https://', 'http://');
  }
  return url;
}

interface PresenceSettingsModalProps {
  camera: {
    camera_id: string | null;
    webrtc_out?: string | null;
    hls_out?: string | null;
    status?: {
      resolution?: { width?: number; height?: number };
      src_width?: number;
      src_height?: number;
      width?: number;
      height?: number;
    };
  };
  onClose: () => void;
  onUpdated?: (zone: PresenceZone | null) => void;
}

interface RoiDrawState {
  isDrawing: boolean;
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  rect: { x: number; y: number; w: number; h: number } | null;
}

const PresenceSettingsModal: React.FC<PresenceSettingsModalProps> = ({ camera, onClose, onUpdated }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [loading, setLoading] = useState(true);
  const [timeoutSec, setTimeoutSec] = useState(String(DEFAULT_PRESENCE_SETTINGS.timeout));
  const [intervalSec, setIntervalSec] = useState(String(DEFAULT_PRESENCE_SETTINGS.interval));
  const [schedule, setSchedule] = useState<PresenceScheduleInterval[]>([...DEFAULT_PRESENCE_SETTINGS.schedule]);
  const [videoWidth, setVideoWidth] = useState(1920);
  const [videoHeight, setVideoHeight] = useState(1080);
  const [roiState, setRoiState] = useState<RoiDrawState>({
    isDrawing: false,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    rect: null,
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error' | 'loading' | null; message: string }>({
    type: null,
    message: '',
  });
  const [savedZone, setSavedZone] = useState<PresenceZone | null>(null);

  const streamUrl = useMemo(
    () => resolveStreamUrl(camera.webrtc_out, camera.hls_out),
    [camera.webrtc_out, camera.hls_out]
  );

  useEffect(() => {
    if (!camera.status) return;
    const s = camera.status;
    if (s.resolution?.width && s.resolution?.height) {
      setVideoWidth(s.resolution.width);
      setVideoHeight(s.resolution.height);
    } else if (s.src_width && s.src_height) {
      setVideoWidth(s.src_width);
      setVideoHeight(s.src_height);
    } else if (s.width && s.height) {
      setVideoWidth(s.width);
      setVideoHeight(s.height);
    }
  }, [camera.status]);

  const applySettingsToForm = useCallback((settings: PresenceSettings) => {
    setTimeoutSec(String(settings.timeout));
    setIntervalSec(String(settings.interval));
    setSchedule(settings.schedule.length > 0 ? settings.schedule : [...DEFAULT_PRESENCE_SETTINGS.schedule]);
    setSavedZone(settings.zone);
  }, []);

  useEffect(() => {
    if (!camera.camera_id) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    camerasAPI
      .getPresenceSettings(camera.camera_id)
      .then((res) => {
        if (cancelled) return;
        if (res.success) {
          applySettingsToForm(normalizePresenceSettings(res.settings ?? res));
        }
      })
      .catch(() => {
        if (!cancelled) applySettingsToForm(DEFAULT_PRESENCE_SETTINGS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [camera.camera_id, applySettingsToForm]);

  const convertVideoToCanvasCoords = useCallback(
    (videoX: number, videoY: number, videoW: number, videoH: number, canvas: HTMLCanvasElement) => {
      const videoAspect = videoWidth / videoHeight;
      const canvasAspect = canvas.width / canvas.height;

      let videoDisplayWidth: number;
      let videoDisplayHeight: number;
      let offsetX: number;
      let offsetY: number;

      if (canvasAspect > videoAspect) {
        videoDisplayHeight = canvas.height;
        videoDisplayWidth = canvas.height * videoAspect;
        offsetX = (canvas.width - videoDisplayWidth) / 2;
        offsetY = 0;
      } else {
        videoDisplayWidth = canvas.width;
        videoDisplayHeight = canvas.width / videoAspect;
        offsetX = 0;
        offsetY = (canvas.height - videoDisplayHeight) / 2;
      }

      const scaleX = videoDisplayWidth / videoWidth;
      const scaleY = videoDisplayHeight / videoHeight;

      return {
        x: offsetX + videoX * scaleX,
        y: offsetY + videoY * scaleY,
        w: videoW * scaleX,
        h: videoH * scaleY,
      };
    },
    [videoWidth, videoHeight]
  );

  const convertCanvasToVideoCoords = useCallback(
    (canvasX: number, canvasY: number, canvasW: number, canvasH: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0, w: 0, h: 0 };

      const videoAspect = videoWidth / videoHeight;
      const canvasAspect = canvas.width / canvas.height;

      let videoDisplayWidth: number;
      let videoDisplayHeight: number;
      let offsetX: number;
      let offsetY: number;

      if (canvasAspect > videoAspect) {
        videoDisplayHeight = canvas.height;
        videoDisplayWidth = canvas.height * videoAspect;
        offsetX = (canvas.width - videoDisplayWidth) / 2;
        offsetY = 0;
      } else {
        videoDisplayWidth = canvas.width;
        videoDisplayHeight = canvas.width / videoAspect;
        offsetX = 0;
        offsetY = (canvas.height - videoDisplayHeight) / 2;
      }

      const scaleX = videoWidth / videoDisplayWidth;
      const scaleY = videoHeight / videoDisplayHeight;

      const x = Math.round((canvasX - offsetX) * scaleX);
      const y = Math.round((canvasY - offsetY) * scaleY);
      const w = Math.round(canvasW * scaleX);
      const h = Math.round(canvasH * scaleY);

      return {
        x: Math.max(0, Math.min(x, videoWidth)),
        y: Math.max(0, Math.min(y, videoHeight)),
        w: Math.max(0, Math.min(w, videoWidth - Math.max(0, x))),
        h: Math.max(0, Math.min(h, videoHeight - Math.max(0, y))),
      };
    },
    [videoWidth, videoHeight]
  );

  const drawZoneOnCanvas = useCallback((zone: PresenceZone | null) => {
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0 || canvas.height === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (!zone) return;

    const canvasRect = convertVideoToCanvasCoords(zone.x, zone.y, zone.w, zone.h, canvas);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(canvasRect.x, canvasRect.y, canvasRect.w, canvasRect.h);
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 3;
    ctx.strokeRect(canvasRect.x, canvasRect.y, canvasRect.w, canvasRect.h);

    setRoiState({
      isDrawing: false,
      startX: canvasRect.x,
      startY: canvasRect.y,
      currentX: canvasRect.x + canvasRect.w,
      currentY: canvasRect.y + canvasRect.h,
      rect: canvasRect,
    });
  }, [convertVideoToCanvasCoords]);

  useEffect(() => {
    if (!savedZone || !streamUrl) return;
    const t = setTimeout(() => drawZoneOnCanvas(savedZone), 900);
    return () => clearTimeout(t);
  }, [savedZone, streamUrl, drawZoneOnCanvas]);

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas || !streamUrl || !camera.camera_id) return;

    if (iframeRef.current?.parentNode) {
      iframeRef.current.parentNode.removeChild(iframeRef.current);
      iframeRef.current = null;
    }

    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    iframeRef.current = iframe;
    container.insertBefore(iframe, canvas);

    const updateCanvasSize = () => {
      if (!iframe || !canvas) return;
      const rect = iframe.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      canvas.width = rect.width;
      canvas.height = rect.height;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    iframe.addEventListener('load', () => {
      setTimeout(updateCanvasSize, 100);
      setTimeout(updateCanvasSize, 500);
      setTimeout(updateCanvasSize, 1000);
    });

    setTimeout(updateCanvasSize, 50);
    window.addEventListener('resize', updateCanvasSize);

    const handleMouseDown = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      setRoiState({
        isDrawing: true,
        startX: e.clientX - rect.left,
        startY: e.clientY - rect.top,
        currentX: e.clientX - rect.left,
        currentY: e.clientY - rect.top,
        rect: null,
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      setRoiState((prev) => {
        if (!prev.isDrawing) return prev;
        const rect = canvas.getBoundingClientRect();
        const currentX = e.clientX - rect.left;
        const currentY = e.clientY - rect.top;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const x = Math.min(prev.startX, currentX);
          const y = Math.min(prev.startY, currentY);
          const w = Math.abs(currentX - prev.startX);
          const h = Math.abs(currentY - prev.startY);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.clearRect(x, y, w, h);
          ctx.strokeStyle = '#10b981';
          ctx.lineWidth = 3;
          ctx.strokeRect(x, y, w, h);
        }
        return { ...prev, currentX, currentY };
      });
    };

    const handleMouseUp = () => {
      setRoiState((prev) => {
        if (!prev.isDrawing) return prev;
        const x = Math.min(prev.startX, prev.currentX);
        const y = Math.min(prev.startY, prev.currentY);
        const w = Math.abs(prev.currentX - prev.startX);
        const h = Math.abs(prev.currentY - prev.startY);
        if (w > 10 && h > 10) {
          return { ...prev, isDrawing: false, rect: { x, y, w, h } };
        }
        return { ...prev, isDrawing: false, rect: null };
      });
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      if (iframeRef.current?.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
      iframeRef.current = null;
    };
  }, [streamUrl, camera.camera_id]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || roiState.isDrawing || !roiState.rect) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y, w, h } = roiState.rect;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.clearRect(x, y, w, h);
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y, w, h);
  }, [roiState]);

  const handleAddInterval = () => {
    setSchedule((prev) => [...prev, { start: '09:00', end: '18:00' }]);
  };

  const handleRemoveInterval = (index: number) => {
    setSchedule((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!camera.camera_id) return;

    const timeout = parseInt(timeoutSec, 10);
    const interval = parseInt(intervalSec, 10);
    if (Number.isNaN(timeout) || timeout < 0) {
      setStatus({ type: 'error', message: 'Таймаут отсутствия не может быть отрицательным' });
      return;
    }
    if (Number.isNaN(interval) || interval < 0) {
      setStatus({ type: 'error', message: 'Интервал повторного скриншота не может быть отрицательным' });
      return;
    }

    let zone: PresenceZone | null = null;
    if (roiState.rect) {
      zone = convertCanvasToVideoCoords(roiState.rect.x, roiState.rect.y, roiState.rect.w, roiState.rect.h);
      if (zone.w <= 0 || zone.h <= 0) {
        zone = null;
      }
    }

    setStatus({ type: 'loading', message: 'Сохранение...' });

    try {
      const res = await camerasAPI.setPresenceSettings(camera.camera_id, {
        timeout,
        interval,
        schedule,
        zone,
      });

      if (!res.success) {
        setStatus({ type: 'error', message: res.message || 'Не удалось сохранить настройки' });
        return;
      }

      setStatus({ type: 'success', message: 'Настройки сохранены' });
      setTimeout(() => {
        onUpdated?.(zone);
        onClose();
      }, 600);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ошибка сохранения';
      setStatus({ type: 'error', message });
    }
  };

  const zoneCoords = roiState.rect
    ? convertCanvasToVideoCoords(roiState.rect.x, roiState.rect.y, roiState.rect.w, roiState.rect.h)
    : null;

  return (
    <div className="roi-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="roi-modal-content">
        <div className="roi-modal-header">
          <h2>
            <img src={presenceIconUrl} alt="" className="roi-modal-title-icon" />
            Контроль присутствия
          </h2>
          <button type="button" className="roi-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        {loading && <p style={{ color: '#6b7280', margin: '0 0 12px' }}>Загрузка настроек...</p>}

        {!loading && (
          <>
            <div className="roi-controls">
              <div className="roi-control-group">
                <label htmlFor="presence-timeout">Таймаут отсутствия (секунды)</label>
                <input
                  id="presence-timeout"
                  type="number"
                  min={0}
                  className="roi-input"
                  value={timeoutSec}
                  onChange={(e) => setTimeoutSec(e.target.value)}
                />
              </div>
              <div className="roi-control-group">
                <label htmlFor="presence-interval">Повтор скриншота (секунды)</label>
                <input
                  id="presence-interval"
                  type="number"
                  min={0}
                  className="roi-input"
                  value={intervalSec}
                  onChange={(e) => setIntervalSec(e.target.value)}
                />
              </div>
            </div>

            <h3 className="presence-section-title">Рабочие интервалы</h3>
            <div className="presence-schedule-list">
              {schedule.map((item, index) => (
                <div key={`${index}-${item.start}`} className="presence-schedule-row">
                  <label>
                    Начало:
                    <input
                      type="time"
                      value={item.start}
                      onChange={(e) =>
                        setSchedule((prev) =>
                          prev.map((row, i) => (i === index ? { ...row, start: e.target.value } : row))
                        )
                      }
                    />
                  </label>
                  <label>
                    Конец:
                    <input
                      type="time"
                      value={item.end}
                      onChange={(e) =>
                        setSchedule((prev) =>
                          prev.map((row, i) => (i === index ? { ...row, end: e.target.value } : row))
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    className="presence-schedule-remove"
                    onClick={() => handleRemoveInterval(index)}
                    aria-label="Удалить интервал"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="presence-add-interval" onClick={handleAddInterval}>
              + Добавить интервал
            </button>
          </>
        )}

        <h3 className="presence-section-title">Зона рабочего места</h3>
        <p className="presence-zone-hint">Если не задана — используется весь кадр</p>

        {streamUrl ? (
          <div className="roi-video-container" ref={containerRef}>
            <canvas ref={canvasRef} className="roi-canvas-overlay" />
          </div>
        ) : (
          <div className="roi-no-video">Видео поток недоступен для выбора зоны</div>
        )}

        {zoneCoords && zoneCoords.w > 0 && zoneCoords.h > 0 && (
          <div className="roi-info">
            <strong>Выбранная зона:</strong>
            <br />
            X: {zoneCoords.x}, Y: {zoneCoords.y}, Ширина: {zoneCoords.w}, Высота: {zoneCoords.h}
          </div>
        )}

        {status.type && (
          <div className={`roi-status roi-status--${status.type}`}>{status.message}</div>
        )}

        <div className="roi-buttons">
          <button
            type="button"
            className="roi-btn roi-btn--primary"
            onClick={handleSave}
            disabled={loading || status.type === 'loading'}
          >
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.save} alt="" className="roi-btn__icon" width={20} height={20} />
              Сохранить
            </span>
          </button>
          <button
            type="button"
            className="roi-btn roi-btn--secondary"
            onClick={() => setRoiState((prev) => ({ ...prev, rect: null, isDrawing: false }))}
          >
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.clear} alt="" className="roi-btn__icon" width={20} height={20} />
              Очистить зону
            </span>
          </button>
          <button type="button" className="roi-btn roi-btn--secondary" onClick={onClose}>
            <span className="roi-btn__inner">
              <img src={roiBtnIcon.cancel} alt="" className="roi-btn__icon" width={18} height={18} />
              Отмена
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PresenceSettingsModal;
